export const WEB_APP_ORIGIN = "https://app.kajikajy.com";
const URL_SAFE_256 = /^[A-Za-z0-9_-]{43}$/;
const HANDOFF_TIMEOUT_MS = 120_000;

export type WebSessionFailure = "popup-blocked" | "failed" | "cancelled";

export class WebSessionHandoffError extends Error {
  constructor(readonly reason: WebSessionFailure) {
    super(reason);
    this.name = "WebSessionHandoffError";
  }
}

export interface WebSessionTicket {
  readonly ticket: string;
  readonly expiresAt: string;
}

export interface WebSessionHandoff {
  readonly result: Promise<void>;
  cancel(): void;
}

// Called directly by the click handler, before awaiting Firebase or App Check.
// Credentials and PKCE values never enter the URL or browser storage.
export function startWebSessionHandoff(options: {
  readonly host: Window;
  readonly begin: (challenge: string) => Promise<WebSessionTicket>;
  readonly isCurrentSession: () => boolean;
}): WebSessionHandoff {
  const { host } = options;
  let popup: Window | null = null;
  let settled = false;
  let requesting = false;
  let sent = false;
  let nonce: string | null = null;
  let timeout: number | undefined;
  let monitor: number | undefined;
  let resolveResult!: () => void;
  let rejectResult!: (error: WebSessionHandoffError) => void;
  const result = new Promise<void>((resolve, reject) => {
    resolveResult = resolve;
    rejectResult = reject;
  });

  const cleanup = () => {
    host.removeEventListener("message", onMessage);
    if (timeout !== undefined) host.clearTimeout(timeout);
    if (monitor !== undefined) host.clearInterval(monitor);
  };
  const finish = (failure?: WebSessionFailure) => {
    if (settled) return;
    settled = true;
    cleanup();
    if (failure) {
      try {
        if (nonce && popup && !popup.closed) {
          popup.postMessage({ type: "kajikajy:web-session-error", nonce }, WEB_APP_ORIGIN);
        }
      } catch {
        // A closed or detached popup must not leave the account button waiting.
      }
      try {
        popup?.close();
      } catch {
        // Only the window created for this pending handshake is ever closed.
      }
      rejectResult(new WebSessionHandoffError(failure));
    } else {
      resolveResult();
    }
  };
  const onMessage = (event: MessageEvent) => {
    if (settled || !popup || event.source !== popup || event.origin !== WEB_APP_ORIGIN) return;
    const data: unknown = event.data;
    if (!data || typeof data !== "object") return;
    const message = data as Record<string, unknown>;
    if (message.type === "kajikajy:web-session-complete" && sent && message.nonce === nonce) {
      finish();
      return;
    }
    if (message.type === "kajikajy:web-session-error" && message.nonce === nonce && nonce) {
      finish("failed");
      return;
    }
    if (requesting || message.type !== "kajikajy:web-session-ready" ||
        typeof message.nonce !== "string" || !URL_SAFE_256.test(message.nonce) ||
        typeof message.codeChallenge !== "string" || !URL_SAFE_256.test(message.codeChallenge)) return;
    nonce = message.nonce;
    requesting = true;
    if (!options.isCurrentSession()) {
      finish("cancelled");
      return;
    }
    void options.begin(message.codeChallenge).then((response) => {
      if (settled) return;
      const expiresAt = Date.parse(response.expiresAt);
      if (!options.isCurrentSession() || !popup || popup.closed) {
        finish("cancelled");
        return;
      }
      // The server enforces its own 90-second expiry. Comparing it with the
      // computer clock would reject valid sessions on a clock-skewed device.
      if (typeof response.ticket !== "string" || !URL_SAFE_256.test(response.ticket) ||
          !Number.isFinite(expiresAt) || new Date(expiresAt).toISOString() !== response.expiresAt) {
        finish("failed");
        return;
      }
      sent = true;
      popup.postMessage({ type: "kajikajy:web-session-ticket", nonce, ticket: response.ticket }, WEB_APP_ORIGIN);
    }).catch(() => finish("failed"));
  };

  try {
    const uniqueName = `kajikajy-web-${host.crypto.randomUUID()}`;
    host.addEventListener("message", onMessage);
    // The opener is deliberately retained for this exact-origin PKCE handshake.
    popup = host.open(`${WEB_APP_ORIGIN}/#/web-session`, uniqueName);
    if (!popup) {
      finish("popup-blocked");
    } else {
      timeout = host.setTimeout(() => finish("failed"), HANDOFF_TIMEOUT_MS);
      monitor = host.setInterval(() => {
        if (!options.isCurrentSession() || popup?.closed) finish("cancelled");
      }, 500);
      popup.focus();
    }
  } catch {
    finish("failed");
  }
  return { result, cancel: () => finish("cancelled") };
}
